import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import LoginForm from './components/LoginForm';
import LoadingSpinner from './components/LoadingSpinner';
import DemoCredentials from './components/DemoCredentials';

const LoginAuthentication = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [isPreview, setIsPreview] = useState(false);
  const [showVideoCover, setShowVideoCover] = useState(true);
  const { signIn, loading, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const videoRef = useRef(null);

  useEffect(() => {
    setIsPreview(new URLSearchParams(window.location.search).get('preview') === 'true');
  }, []);

  // Redirect if already authenticated
  useEffect(() => {
    if (!loading && isAuthenticated) {
      navigate('/product-order-management');
    }
  }, [isAuthenticated, loading, navigate]);

  const handleLogin = async (credentials) => {
    const { username, password } = credentials;
    
    // Clear previous errors
    setError('');
    setIsSubmitting(true);

    try {
      const result = await signIn?.(username, password);
      
      if (result?.success) {
        navigate('/product-order-management');
      } else {
        setError(result?.error || 'Errore durante l\'accesso');
      }
    } catch (error) {
      setError('Errore di connessione. Riprova.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleStartVideo = async () => {
    setShowVideoCover(false);

    try {
      await videoRef.current?.play();
    } catch (playError) {
      setShowVideoCover(true);
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-8 sm:px-6 lg:px-8">
      {/* Header */}
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="text-center">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Order Module
          </h1>
          <h2 className="text-xl text-gray-600 mb-8">
            Sistema Ordini
          </h2>
        </div>
      </div>

      {!isPreview ? (
        <div className="sm:mx-auto sm:w-full sm:max-w-3xl mb-6 px-4 sm:px-0">
          <div className="overflow-hidden rounded-xl border border-gray-200 bg-white p-4 shadow-xl sm:p-5">
            <h3 className="text-lg font-semibold text-gray-900 sm:text-xl">
              A cosa serve questa WebApp?
            </h3>
            <p className="mt-2 text-sm leading-6 text-gray-600">
              Catalogo sincronizzato, listini dedicati per rivenditore e invio ordine diretto su WhatsApp in un unico flusso.
            </p>
            <div className="relative mt-4 overflow-hidden rounded-xl border border-gray-200 bg-gray-950 shadow-lg">
              {showVideoCover ? (
                <button
                  type="button"
                  onClick={handleStartVideo}
                  className="group absolute inset-0 z-10 flex w-full items-center justify-center overflow-hidden text-white"
                  aria-label="Avvia video dimostrativo"
                >
                  <img
                    src="/Modulo_Ordine_Rivenditore-poster.jpg"
                    alt="Anteprima video dimostrativo"
                    className="h-full w-full object-cover"
                  />
                  <span className="absolute inset-0 bg-slate-950/35" />
                  <span className="absolute left-4 top-4 rounded-full bg-white/90 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-slate-900 shadow">
                    Video dimostrativo
                  </span>
                  <span className="absolute flex h-24 w-24 items-center justify-center rounded-full border-4 border-white bg-black/70 shadow-2xl backdrop-blur-sm transition-transform duration-200 group-hover:scale-105">
                    <span className="ml-1 border-y-[16px] border-l-[24px] border-y-transparent border-l-white drop-shadow-[0_0_6px_rgba(255,255,255,0.45)]" />
                  </span>
                  <span className="absolute bottom-5 left-1/2 w-[calc(100%-2rem)] -translate-x-1/2 rounded-2xl bg-slate-950/60 px-4 py-3 text-center shadow-lg backdrop-blur-sm">
                    <span className="block text-sm font-semibold sm:text-base">
                      Guarda la panoramica della WebApp
                    </span>
                    <span className="mt-1 block text-xs text-white/80 sm:text-sm">
                      Tocca o clicca per avviare il video
                    </span>
                  </span>
                </button>
              ) : null}

              <video
                ref={videoRef}
                controls
                playsInline
                webkit-playsinline="true"
                preload="metadata"
                poster="/Modulo_Ordine_Rivenditore-poster.jpg"
                src="/Modulo_Ordine_Rivenditore.mp4"
                onPlay={() => setShowVideoCover(false)}
                onEnded={() => setShowVideoCover(true)}
                className="w-full"
              />
            </div>
          </div>
        </div>
      ) : null}

      {/* Login Card */}
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow-xl rounded-lg sm:px-10">
          <div className="mb-6">
            <h3 className="text-2xl font-semibold text-gray-900 text-center">
              Accedi al Sistema
            </h3>
            <p className="mt-2 text-sm text-gray-600 text-center">
              Inserisci le tue credenziali per accedere al modulo ordini
            </p>
          </div>

          <LoginForm 
            onSubmit={handleLogin}
            isLoading={isSubmitting}
            error={error}
          />
          <DemoCredentials />
        </div>
      </div>

      {/* Footer */}
      <div className="mt-4 text-center text-sm text-gray-500">
        <p>Accesso sicuro al modulo gestione ordini</p>
      </div>
    </div>
  );
};

export default LoginAuthentication;
